import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

function SimpleDone({ task, onDone, note }) {
  const [move, setMove] = useState(false);
  const [due, setDue] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (fn, msg) => { setBusy(true); try { await fn(); toast.success(msg); onDone(); } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update"); } finally { setBusy(false); } };
  const complete = (outcome) => run(() => (task.sop
    ? api.put(`/crm/tasks/${task._id}`, { status: "done", note: [outcome === "Skipped" ? "Skipped" : "", note || ""].filter(Boolean).join(" · ") || undefined })
    : api.post(`/crm/tasks/${task._id}/complete`, { outcome, note: note || "" })), outcome === "Skipped" ? "Task skipped" : "Task done");
  return (
    <div className="space-y-1.5 border-t border-[var(--border)] pt-2" data-testid="task-simple-actions">
      <div className="text-[11px] text-neutral-500">Or just close this task</div>
      <div className="flex flex-wrap gap-1.5">
        <button className="btn text-xs" disabled={busy} onClick={() => complete("Done")} data-testid="task-simple-done">Done</button>
        <button className="btn text-xs" disabled={busy || !note} title={note ? "" : "Type a note below first"} onClick={() => complete("Done")} data-testid="task-simple-done-note">Done with note</button>
        <button className="btn text-xs" disabled={busy} onClick={() => setMove(!move)} data-testid="task-simple-move">Move date</button>
        <button className="btn text-xs" disabled={busy} onClick={() => complete("Skipped")} data-testid="task-simple-skip">Skip</button>
      </div>
      {move && <div className="flex gap-2">
        <input type="date" className="flex-1 px-2.5 py-1.5 rounded border text-sm" value={due} onChange={(e) => setDue(e.target.value)} data-testid="task-simple-due" />
        <button className="btn btn-primary text-xs" disabled={!due || busy} onClick={() => run(() => api.put(`/crm/tasks/${task._id}`, { due: `${due}T10:00:00+05:30` }), "Date moved")} data-testid="task-simple-move-save">Save</button>
      </div>}
    </div>
  );
}

export default function SopOutcomeDialog({ task, onClose, onDone }) {
  const [d, setD] = useState(null);
  const [pick, setPick] = useState(null);
  const [extra, setExtra] = useState({ meeting_type: "Google Meet" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/crm/sop/tasks/${task._id}/outcomes`).then((r) => setD(r.data)); }, [task._id]);
  const submit = async (o) => {
    setBusy(true);
    try {
      const body = { outcome: o.outcome, task_id: task._id, note: extra.note };
      if (o.needs === "slot") Object.assign(body, { slot: new Date(extra.slot).toISOString(), meeting_type: extra.meeting_type });
      if (o.needs === "client_date") body.client_date = extra.client_date;
      if (o.needs === "lost_reason") body.lost_reason = extra.lost_reason;
      const { data } = await api.post(`/crm/sop/leads/${task.lead_id}/outcome`, body);
      toast.success(data.note); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't log outcome"); }
    finally { setBusy(false); }
  };
  const choose = (o) => (o.needs ? setPick(o) : submit(o));
  const ready = pick && ((pick.needs === "slot" && extra.slot) || (pick.needs === "client_date" && extra.client_date) || (pick.needs === "lost_reason" && extra.lost_reason));
  const cls = "w-full px-2.5 py-1.5 rounded border text-sm";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[var(--panel)] border-[var(--border-strong)] max-w-md" data-testid="sop-outcome-dialog">
        <DialogHeader><DialogTitle className="text-base font-medium">What happened?</DialogTitle></DialogHeader>
        <div className="text-xs text-neutral-500 -mt-2">{task.label}{task.lead_name ? ` · ${task.lead_name}` : ""}</div>
        {d?.script && <div className="text-xs text-neutral-300 panel-2 p-2.5 whitespace-pre-wrap" data-testid="sop-script">{d.script}</div>}
        {!d ? <div className="text-xs text-neutral-500">Loading…</div> : !d.rows.length ? <div className="text-xs text-neutral-500">No SOP outcomes at this step — close it below.</div> : !pick ? (
          <div className="flex flex-col gap-1.5" data-testid="sop-outcome-list">
            {d.rows.map((o) => (
              <button key={o.outcome} className="btn justify-between text-left" disabled={busy} onClick={() => choose(o)} data-testid={`sop-outcome-${o.outcome.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                <span>{o.outcome}</span><span className="text-[10px] text-neutral-500 ml-2 text-right">→ {o.next}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="space-y-2" data-testid="sop-outcome-extra">
            <div className="text-sm">{pick.outcome}</div>
            {pick.needs === "slot" && <>
              <input type="datetime-local" className={cls} onChange={(e) => setExtra({ ...extra, slot: e.target.value })} data-testid="sop-slot" />
              <select className={cls} value={extra.meeting_type} onChange={(e) => setExtra({ ...extra, meeting_type: e.target.value })} data-testid="sop-meeting-type">
                <option>Google Meet</option><option>Phone call</option>
              </select>
            </>}
            {pick.needs === "client_date" && <input type="date" className={cls} onChange={(e) => setExtra({ ...extra, client_date: e.target.value })} data-testid="sop-client-date" />}
            {pick.needs === "lost_reason" && (
              <select className={cls} value={extra.lost_reason || ""} onChange={(e) => setExtra({ ...extra, lost_reason: e.target.value })} data-testid="sop-lost-reason">
                <option value="">Lost reason…</option>{d.lost_reasons.map((r) => <option key={r}>{r}</option>)}
              </select>
            )}
            <div className="flex gap-2 justify-end">
              <button className="btn-ghost btn text-xs" onClick={() => setPick(null)}>Back</button>
              <button className="btn btn-primary text-xs" disabled={!ready || busy} onClick={() => submit(pick)} data-testid="sop-outcome-confirm">Log outcome</button>
            </div>
          </div>
        )}
        <textarea rows={2} className={cls} placeholder="Note (optional)" onChange={(e) => setExtra({ ...extra, note: e.target.value })} data-testid="sop-note" />
        {!pick && <SimpleDone task={task} onDone={onDone} note={extra.note} />}
        <button className="btn-ghost btn text-xs self-start" onClick={onClose} data-testid="sop-later-btn">Later</button>
      </DialogContent>
    </Dialog>
  );
}
